/**
 * The Games page's state and what it does (place manager, unit 8), apart from
 * React so each path runs in tests with the samples and fake timers:
 *   - the read: the brand's list and the catalog, when the page opens; again
 *     after each write; at once, in a few seconds and in half a minute after a
 *     lost answer (only the latest read lands);
 *   - the draft (the ticks) and the step: edit → review (one change, frozen:
 *     Apply sends exactly what its line names) → applying → back to edit, or
 *     lost (no second Apply: Close, then the list as read again);
 *   - the change applied here and its Undo, for five minutes.
 * One write at a time (an Apply or an Undo). A second Apply finds the step
 * already "applying" and sends nothing (checked here, synchronously, never on
 * a render's stale copy); `singleFlight` behind it gets the first's answer.
 * Undo waits while a change is reviewed (its Apply would put back what Undo
 * took away). `use-brand-games.ts` binds it to the page.
 */
import { errorMessage } from "@/lib/backend-error";
import type { Load } from "@/features/places/use-places";
import { ONE_WRITE_LINE, REREADS_AFTER_LOSS_MS, oneWriteAtATime, singleFlight, undoOffered, type WriteGuards } from "@/features/writes/writes";
import {
  LATER_CHANGE_LINE,
  applyChange,
  changeOf,
  needsSecondTap,
  undoGamesChange,
  type ApplyOutcome,
  type BrandGamesApi,
  type BrandGamesRead,
  type GamesChange,
  type GamesChanged,
} from "@/features/games/brand-games";

/** Where the page stands: ticking (with a line about the last Apply not sent,
 *  when the list had changed since or the brand is gone); the review of one
 *  change, while it applies; or its answer lost. */
export type GamesStep =
  | { kind: "edit"; notice?: string }
  | { kind: "review" | "applying"; change: GamesChange; confirmed: boolean; failed: string | null }
  | { kind: "lost"; change: GamesChange; line: string };

export interface GamesState {
  /** The brand's list and the catalog; null data: the brand is not visible. */
  load: Load<BrandGamesRead | null>;
  /** The brand is being read again (after a write, or a lost answer). */
  rereading: boolean;
  /** The ticks; null: as read. */
  draft: ReadonlySet<string> | null;
  step: GamesStep;
  /** The change applied here, while its undo matters; null before any. */
  changed: GamesChanged | null;
}

export interface GamesStore {
  getState(): GamesState;
  subscribe(listener: () => void): () => void;
  /** Read the brand (the page opened). */
  start(): void;
  /** The page went: pending reads again are cleared and nothing lands after. */
  dispose(): void;
  tick(slug: string, on: boolean): void;
  review(): void;
  back(): void;
  discard(): void;
  /** The second tap on the review line (a list that leaves no catalog game showing). */
  confirmEmpty(): void;
  apply(): Promise<void>;
  /** Close a lost answer's review: the ticks go, the list shows as read again. */
  close(): void;
  undo(): Promise<void>;
}

export interface GamesStoreDeps {
  api: BrandGamesApi;
  /** The samples are served (`?fixtures=1`): reads carry no time. */
  sample: boolean;
  /** Whether a failure may have gone through (`isLostAnswer`). */
  isLost: (error: unknown) => boolean;
  /** Run `run` after `delay` ms (tests pass their own); cleared on dispose. */
  after?: (delay: number, run: () => void) => void;
  clock?: () => number;
}

/** The ticks shown: the draft, or the list as read. */
export function tickedOf(state: GamesState): ReadonlySet<string> {
  return state.draft ?? new Set(state.load.state === "ready" ? (state.load.data?.slugs ?? []) : []);
}

export function createGamesStore(deps: GamesStoreDeps): GamesStore {
  const clock = deps.clock ?? Date.now;
  let state: GamesState = { load: { state: "loading" }, rereading: false, draft: null, step: { kind: "edit" }, changed: null };
  const listeners = new Set<() => void>();
  let disposed = false;
  const pending = new Set<ReturnType<typeof setTimeout>>();
  const after =
    deps.after ??
    ((delay: number, run: () => void) => {
      const id = setTimeout(() => {
        pending.delete(id);
        run();
      }, delay);
      pending.add(id);
    });

  const set = (next: Partial<GamesState> | ((s: GamesState) => Partial<GamesState>)) => {
    if (disposed) return;
    state = { ...state, ...(typeof next === "function" ? next(state) : next) };
    listeners.forEach((l) => l());
  };
  const data = () => (state.load.state === "ready" ? state.load.data : null);

  // The latest read asked for: only its answer lands.
  let latest = 0;
  const read = (first: boolean) => {
    // The page went: nothing more is read.
    if (disposed) return;
    const request = ++latest;
    set(first ? { load: { state: "loading" } } : { rereading: true });
    deps.api.read().then(
      (answer) => {
        if (request !== latest) return;
        set({
          load: { state: "ready", data: answer, sample: deps.sample, readAt: deps.sample ? null : clock(), refreshFailed: false },
          rereading: false,
        });
      },
      (e: unknown) => {
        if (request !== latest) return;
        console.warn("Could not read the brand's games:", e);
        // A failed read again keeps the last good read, said as such.
        set((s) => ({
          load:
            s.load.state === "ready" && !first
              ? { ...s.load, refreshFailed: true }
              : { state: "error", message: errorMessage(e, "Could not read the games.") },
          rereading: false,
        }));
      },
    );
  };

  /** The list as a write left it, shown at once (the read after confirms it). */
  const showList = (slugs: readonly string[]) =>
    set((s) => (s.load.state === "ready" && s.load.data ? { load: { ...s.load, data: { ...s.load.data, slugs: [...slugs] } } } : {}));

  const writes = oneWriteAtATime();
  const guards: WriteGuards = {
    reread: () => REREADS_AFTER_LOSS_MS.forEach((delay) => after(delay, () => !disposed && read(false))),
    isLost: deps.isLost,
  };
  const send = singleFlight((change: GamesChange) =>
    writes.run(() => applyChange(change, deps.api, guards, clock)).catch(
      // Refused before sending: another write here is still going.
      (e: unknown): ApplyOutcome => ({ failed: errorMessage(e, ONE_WRITE_LINE) }),
    ),
  );

  const undoing = () => state.changed?.undo === "undoing";

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    start() {
      disposed = false;
      read(true);
    },
    dispose() {
      disposed = true;
      pending.forEach(clearTimeout);
      pending.clear();
    },

    tick(slug, on) {
      if (state.step.kind !== "edit" || undoing() || !data()) return;
      const next = new Set(tickedOf(state));
      if (on) next.add(slug);
      else next.delete(slug);
      set({ draft: next, step: { kind: "edit" } });
    },
    review() {
      const shown = data();
      const change = shown && changeOf(shown, tickedOf(state));
      if (state.step.kind !== "edit" || undoing() || !change) return;
      set({ step: { kind: "review", change, confirmed: false, failed: null } });
    },
    back() {
      if (state.step.kind === "review") set({ step: { kind: "edit" } });
    },
    discard() {
      if (state.step.kind === "edit") set({ draft: null, step: { kind: "edit" } });
    },
    confirmEmpty() {
      const step = state.step;
      if (step.kind === "review") set({ step: { ...step, confirmed: true } });
    },
    close() {
      if (state.step.kind === "lost") set({ draft: null, step: { kind: "edit" } });
    },

    async apply() {
      const step = state.step;
      const shown = data();
      if (step.kind !== "review" || !shown || (needsSecondTap(step.change, shown.catalog) && !step.confirmed)) return;
      // At once, before anything is sent: a second press finds "applying".
      set({ step: { ...step, kind: "applying", failed: null } });
      const outcome = await send(step.change);
      if ("changed" in outcome) {
        // The latest write owns the one Undo.
        set({ changed: outcome.changed, draft: null, step: { kind: "edit" } });
        showList(outcome.changed.after);
        read(false);
      } else if ("lost" in outcome) {
        // It may have gone through: whatever the earlier change's note said
        // (its Undo ready, or "Undone: as they were") may no longer hold, and
        // its Undo would take this one back too, unseen. Said so; no Undo.
        set((s) => ({
          step: { kind: "lost", change: step.change, line: outcome.lost },
          changed: s.changed && { ...s.changed, undo: { held: LATER_CHANGE_LINE } },
        }));
      } else if ("stale" in outcome || "gone" in outcome) {
        // The ticks stay; reviewed again, they are diffed against the list as
        // read now. Gone: the read lands no brand, and the page says so.
        set({ step: { kind: "edit", notice: "stale" in outcome ? outcome.stale : outcome.gone } });
        read(false);
      } else set({ step: { ...step, kind: "review", failed: outcome.failed } });
    },

    async undo() {
      // Checked again at the press, whatever is on screen.
      const now = clock();
      const target = state.changed;
      if (state.step.kind !== "edit" || !target || !undoOffered(target, now) || writes.busy) return;
      const setUndo = (undo: GamesChanged["undo"]) =>
        set((s) => (s.changed?.madeAt === target.madeAt ? { changed: { ...s.changed, undo } } : {}));
      setUndo("undoing");
      const undo = await writes
        .run(() => undoGamesChange(target, deps.api, guards, now))
        .catch((e: unknown): GamesChanged["undo"] => ({ failed: errorMessage(e, ONE_WRITE_LINE) }));
      setUndo(undo);
      if (undo === "undone") showList(target.before);
      // Undone, held or gone: show the list (or its absence) as it is now.
      if (undo === "undone" || (typeof undo === "object" && !("failed" in undo))) read(false);
    },
  };
}
